// utils/systemUtils.js
import { 
  DATA_FRESHNESS_THRESHOLDS, 
  STATUS_ICONS, 
  REFRESH_STATUS,
  STATUS_COLORS 
} from '../constants/systemConstants.js';

export const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    // Ensure we're working with a proper Date object
    let date;
    if (typeof timestamp === 'string') {
        // Handle UTC timestamps from database properly
        // If timestamp doesn't end with Z, assume it's UTC from database
        if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
            timestamp = timestamp + 'Z'; // Mark as UTC
        }
        date = new Date(timestamp);
    } else {
        date = new Date(timestamp);
    }
    
    // Check if parsing failed
    if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp:', timestamp);
        return 'Invalid date';
    }
    
    const now = new Date();
    const diffMs = now - date;
    
    // Calculate days using date-only comparison (ignore time)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((nowOnly - dateOnly) / (1000 * 60 * 60 * 24));
    
    // Debug logging for timezone investigation
    
    if (diffDays === 0 && diffMs >= 0) {
        // Same day and in the past
        return 'Today at ' + date.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: false
        });
    } else if (diffDays === 1 && diffMs >= 0) {
        // Yesterday  
        return 'Yesterday at ' + date.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: false
        });
    } else if (diffDays > 1 && diffDays < 7) {
        return `${diffDays} days ago`;
    } else if (diffDays >= 7 && diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays >= 30 && diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''} ago`;
    } else if (diffMs < 0) {
        // Future timestamp - this should not happen!
        console.error('Future timestamp detected:', timestamp, 'Diff:', diffMs);
        return `Future time: ${formatDateDDMMYYYY(timestamp)}`;
    } else {
        // Very old dates
        return formatDateDDMMYYYY(timestamp);
    }
};

export const formatDateDDMMYYYY = (timestamp) => {
    if (!timestamp) return 'unknown';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

export const getDataFreshnessStatus = (lastUpdate) => {
    if (!lastUpdate) return { status: 'unknown', color: 'gray', message: 'No data available' };
    
    const date = new Date(lastUpdate);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < DATA_FRESHNESS_THRESHOLDS.FRESH_HOURS) {
        return { status: 'fresh', color: 'green', message: 'Fresh (< 24h)' };
    } else if (diffHours < DATA_FRESHNESS_THRESHOLDS.STALE_HOURS) {
        return { status: 'stale', color: 'yellow', message: 'Getting stale (1-2 days)' };
    } else if (diffHours < DATA_FRESHNESS_THRESHOLDS.OLD_HOURS) {
        return { status: 'old', color: 'orange', message: `Stale (${Math.floor(diffHours / 24)} days)` };
    } else {
        const diffDays = Math.floor(diffHours / 24);
        return { status: 'critical', color: 'red', message: `Very stale (${diffDays} days)` };
    }
};

export const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
};

export const formatBytes = (bytes) => {
    if (bytes === 0 || bytes === null || bytes === undefined) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const getRefreshStatusIcon = (status) => {
    return STATUS_ICONS[status] || STATUS_ICONS[REFRESH_STATUS.UNKNOWN];
};

// New utility functions for enhanced functionality

export const getStatusColorClasses = (color) => {
    return STATUS_COLORS[color] || STATUS_COLORS.gray;
};

export const formatDatasetInfo = (datasets) => {
    if (!datasets || !Array.isArray(datasets)) {
        return { count: 0, totalSize: 0, types: [] };
    }

    const totalSize = datasets.reduce((sum, dataset) => sum + (dataset.size_bytes || 0), 0);
    const types = [...new Set(datasets.map(d => d.dataset_type))];
    
    return {
        count: datasets.length,
        totalSize,
        types,
        formattedSize: formatBytes(totalSize),
        latestUpdate: datasets.length > 0 
            ? Math.max(...datasets.map(d => new Date(d.created_at || d.generation_date).getTime()))
            : null
    };
};

export const getDatasetHealthStatus = (datasetInfo) => {
    if (!datasetInfo || datasetInfo.count === 0) {
        return { status: 'error', color: 'red', message: 'No datasets available' };
    }

    const { latestUpdate } = datasetInfo;
    if (!latestUpdate) {
        return { status: 'warning', color: 'yellow', message: 'Datasets exist but no timestamp available' };
    }

    const ageHours = (Date.now() - latestUpdate) / (1000 * 60 * 60);
    
    if (ageHours < DATA_FRESHNESS_THRESHOLDS.FRESH_HOURS) {
        return { status: 'fresh', color: 'green', message: 'Fresh datasets available' };
    } else if (ageHours < DATA_FRESHNESS_THRESHOLDS.OLD_HOURS) {
        return { status: 'stale', color: 'yellow', message: 'Datasets getting stale' };
    } else {
        return { status: 'old', color: 'orange', message: 'Datasets are old' };
    }
};
// End of systemUtils.js - removed unused buildRefreshButtonConfig function
